import { AR_VOID_REASONS, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import type { ArInvoiceVoid, ArVoidReason } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

// Router-free AR invoice-void list. The route owns data fetching
// (useArInvoiceVoids) + loading/error handling + the engine decision panel;
// this component owns presentation only — a flat table carrying BOTH the
// accounting `status` and the engine `approvalStatus` per row (Design-Z keeps
// them decoupled). A PENDING (engine-managed) row gets a single
// "ดำเนินการอนุมัติ" action that opens the shared 4-eyes panel — exactly as /ap
// does for engine-managed bills, and the Phase 2b credit-note worklist does.
// A void has NO line items (it is the inverse of the invoice's original JV), so
// this list mirrors CreditNoteList without the line-totals column. Plain
// prototype.css classes only.

// The list endpoint enriches each ArInvoiceVoid header with display names the
// wire `ArInvoiceVoid` type already declares optionally (see
// ar-invoice-voids.service.listVoids). Use the base type directly.
export type VoidListRow = ArInvoiceVoid;

export interface VoidListProps {
  voids: VoidListRow[];
  /** Opens the engine 4-eyes decision panel for a PENDING (engine-managed) void. */
  onDecide: (v: VoidListRow) => void;
}

function reasonLabel(code: string): string {
  return AR_VOID_REASONS[code as ArVoidReason] ?? code;
}

// Accounting-status badge — same vocabulary as the /ap + credit-note status cell.
function StatusBadge({ status }: { status: ArInvoiceVoid['status'] }) {
  switch (status) {
    case 'POSTED':
      return (
        <span className="badge positive">
          <span className="bdot" />
          ลงบัญชีแล้ว
        </span>
      );
    case 'VOID':
      return (
        <span className="badge negative">
          <span className="bdot" />
          ยกเลิก
        </span>
      );
    case 'PENDING':
      return (
        <span className="badge warning">
          <span className="bdot" />
          รออนุมัติ
        </span>
      );
    case 'RETURNED':
      return (
        <span className="badge warning">
          <span className="bdot" />
          ตีกลับแก้ไข
        </span>
      );
    default:
      return (
        <span className="badge info">
          <span className="bdot" />
          ร่าง
        </span>
      );
  }
}

export function VoidList({ voids, onDecide }: VoidListProps) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบยกเลิก</th>
              <th>วันที่ยกเลิก</th>
              <th>ลูกค้า</th>
              <th>ใบแจ้งหนี้อ้างอิง</th>
              <th>เหตุผล</th>
              <th className="num">มูลค่ารวม</th>
              <th>สถานะบัญชี</th>
              <th>สายอนุมัติ</th>
            </tr>
          </thead>
          <tbody>
            {voids.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              voids.map((v) => {
                // A PENDING void sitting in the engine (approvalStatus set)
                // exposes the central 4-eyes decision action — identical gate to
                // the /ap engine-managed row.
                const inEngine = v.approvalStatus != null && v.status === 'PENDING';
                return (
                  <tr key={v.id}>
                    <td>
                      <span className="code">{v.voucherNo}</span>
                    </td>
                    <td>{fmtDate(v.voidDate)}</td>
                    <td>{v.customerName ?? v.customerId}</td>
                    <td className="muted">{v.invoiceVoucherNo ?? '—'}</td>
                    <td className="muted">{reasonLabel(v.reasonCode)}</td>
                    <td className="num tnum">{fmt(toNumber(v.amount), 2)}</td>
                    <td>
                      <StatusBadge status={v.status} />
                    </td>
                    <td>
                      {inEngine ? (
                        <button
                          className="btn primary"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onDecide(v)}
                          title="ดำเนินการอนุมัติ 4 ตา (สายอนุมัติกลาง)"
                        >
                          <Icon name="check" size={12} /> ดำเนินการอนุมัติ
                        </button>
                      ) : v.approvalStatus ? (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {v.approvalStatus}
                        </span>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
